import React, { useEffect, useState } from 'react';
import { useApiClient } from '../../service/API';
import Pill from './Pill';
import ConfirmationModal from '../TicketComponents/ConfirmationModal';
import '../../styling/service-flow-manager.css';
import { Link, useNavigate } from 'react-router-dom';

export default function ListServices() {
  const apiClient = useApiClient();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());
  const navigate = useNavigate();

  // ---- modal state ----
  const [showRetireModal, setShowRetireModal] = useState(false);
  const [pendingRetireService, setPendingRetireService] = useState(null); // serviceType

  async function loadAll() {
    setLoading(true);
    try {
      const res = await apiClient.get(`/config/service-flow`);
      setServices(res.data || []);
    } finally {
      setLoading(false);
    }
  }

  async function retireService(serviceType) {
    setLoading(true);
    try {
      const res = await apiClient.put(`/config/service-flow/retire/${serviceType}`);
      setServices(res.data || []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, []);

  function toggleExpand(serviceType) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(serviceType)) next.delete(serviceType);
      else next.add(serviceType);
      return next;
    });
  }

  // ---- open/close/confirm handlers ----
  function askRetireConfirmation(serviceType) {
    setPendingRetireService(serviceType);
    setShowRetireModal(true);
  }

  function closeRetireModal() {
    setShowRetireModal(false);
    setPendingRetireService(null);
  }

  async function confirmRetireModal() {
    if (!pendingRetireService) return;
    await retireService(pendingRetireService);
    closeRetireModal();
  }

  return (
    <div className="sfm">
      <div className="sfm-container">
        <header style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16}}>
          <div>
            <h1>Service Flows</h1>
            <p>All existing services and their stages.</p>
          </div>
          <div>
            <Link to="/service-flows/new" className="sfm-btn sfm-btn-primary">
              Create New Service
            </Link>
          </div>
        </header>

        {loading ? (
          <section className="sfm-card"><p>Loading…</p></section>
        ) : services.length === 0 ? (
          <section className="sfm-card"><p>No services yet.</p></section>
        ) : (
          services.map((svc)=> {
            const isOpen = expanded.has(svc.serviceType);
            return (
              <section key={svc.serviceType} className="sfm-card">
                {/* Collapsible header */}
                <button
                  className="sfm-collapse-header"
                  onClick={()=>toggleExpand(svc.serviceType)}
                  aria-expanded={isOpen}
                  aria-controls={`svc-body-${svc.serviceType}`}
                >
                  <span className={`sfm-chev ${isOpen ? 'open' : ''}`}>▶</span>
                  <span className="sfm-svc-title">{svc.serviceType}</span>
                  {!isOpen && (
                    <span className="sfm-svc-summary">
                      {svc.stages?.length || 0} stage(s)
                    </span>
                  )}
                </button>

                {/* Actions on header right */}
                <div className="sfm-collapse-actions">
                  <button
                    className="sfm-btn sfm-btn-secondary"
                    onClick={() => navigate(`/service-flows/${encodeURIComponent(svc.serviceType)}/edit`)}
                  >
                    Edit
                  </button>
                  <button
                    className="sfm-btn sfm-btn-destructive"
                    onClick={() => askRetireConfirmation(svc.serviceType)} // <-- changed
                  >
                    Retire Service
                  </button>
                </div>

                {/* Collapsible body */}
                {isOpen && (
                  <div id={`svc-body-${svc.serviceType}`} className="sfm-collapse-body">
                    <table className="sfm-stage-table">
                      <thead>
                        <tr>
                          <th style={{width:48}}>#</th>
                          <th style={{width:'20%'}}>Code</th>
                          <th>Name</th>
                          <th style={{width:'15%'}}>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {svc.stages?.map((s, idx)=>(
                          <tr key={`${s.id}-${idx}`}>
                            <td>{idx+1}</td>
                            <td>{s.code}</td>
                            <td>{s.name}</td>
                            <td>{s.isTerminal ? <Pill>Terminal</Pill> : <Pill>Stage</Pill>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            );
          })
        )}

        {/* ---- Confirmation Modal ---- */}
        <ConfirmationModal
          show={showRetireModal}
          onClose={closeRetireModal}
          onConfirm={confirmRetireModal}
          title="Retire Service?"
          message={
            pendingRetireService
              ? `Are you sure you want to retire "${pendingRetireService}"? This will deactivate its current flow.`
              : 'Are you sure you want to proceed?'
          }
        />
      </div>
    </div>
  );
}
